// First-run onboarding for Cercle.
//
// Someone who has never heard of the app arrives here. Three things have to
// happen, in this order, as fast as possible:
//   1. they understand the promise (a contact card that updates itself),
//   2. they get an identity without filling a form they don't understand,
//   3. they hold their own shareable card — the "aha".
//
// The whole flow lives in a single screen that swaps steps in place (no URL
// change), so the recovery phrase can never land in the address bar and a
// half-finished onboarding leaves nothing behind: the identity is derived in
// the background but only written to disk when the visitor commits at the
// "Faisons connaissance" step (see core/identity prepare/adopt).

import { h } from './dom';
import { icon, type IconName } from './icons';
import { toast } from './toast';
import { adoptIdentity, currentIdentity, markSeedBackedUp, prepareIdentity, type PreparedIdentity } from '../core/identity';
import { avatarFor } from '../core/avatar';
import { emptyProfile, saveMyProfile } from '../core/profile';
import { brand, bullet, ghostButton, primaryButton, shareCard } from './components';
import { openAddFriendSheet } from './addFriendSheet';
import { navigate, pendingInvite } from './router';

type StepName = 'hero' | 'value' | 'how' | 'privacy' | 'you' | 'phrase' | 'ready';

// The full path. `hero` carries no progress bar; the three slides in the middle
// are skippable and swipeable; everything from `you` on is committed flow.
const FLOW: StepName[] = ['hero', 'value', 'how', 'privacy', 'you', 'phrase', 'ready'];
const SWIPEABLE: StepName[] = ['value', 'how', 'privacy'];

// --- Small shared pieces ----------------------------------------------------

function stepTitle(text: string): HTMLElement {
  // tabindex=-1 so each step's heading can take focus when the step changes,
  // which is what tells a screen reader the view moved on.
  return h('h1', { class: 'hero-title onb-title', tabindex: '-1', text });
}

function stepLead(text: string): HTMLElement {
  return h('p', { class: 'lead onb-lead', text });
}

function eyebrow(text: string): HTMLElement {
  return h('p', { class: 'eyebrow', text });
}

function initialsAvatar(name: string, seed: string, cls = 'onb-avatar'): HTMLElement {
  const a = avatarFor(name, seed);
  const el = h('div', { class: cls, text: a.initials });
  // Colours via CSSOM, never a style attribute — strict CSP (ARCHITECTURE §11).
  el.style.background = a.bg;
  el.style.color = a.fg;
  return el;
}

// --- Illustrations (pure DOM + CSS, no images, no innerHTML) ----------------

/**
 * The value slide's centrepiece: a mini contact card whose address swaps to a
 * new one, three dots travelling down, and three contacts ticking over to the
 * new version. It states the whole product in one loop, before a single word
 * is read. Every animation ends on the "updated" frame, so with reduced motion
 * (durations collapsed to ~0) the illustration still reads correctly.
 */
function updateDemo(): HTMLElement {
  const miniCard = h(
    'div',
    { class: 'demo-card' },
    h('span', { class: 'demo-badge', text: 'à jour' }),
    h(
      'div',
      { class: 'demo-head' },
      initialsAvatar('Camille Roux', 'demo-seed-camille', 'demo-avatar'),
      h('div', { class: 'demo-head-text' }, h('span', { class: 'demo-name', text: 'Camille Roux' }), h('span', { class: 'demo-sub', text: '06 12 34 56 78' })),
    ),
    h(
      'div',
      { class: 'demo-field' },
      h('span', { class: 'demo-label', text: 'Adresse' }),
      h(
        'span',
        { class: 'demo-swap' },
        h('span', { class: 'demo-old', text: '12 rue des Lilas' }),
        h('span', { class: 'demo-new', text: '8 avenue Mozart' }),
      ),
    ),
  );

  const flow = h('div', { class: 'demo-flow' }, h('span'), h('span'), h('span'));

  const people = h(
    'div',
    { class: 'demo-people' },
    ...[
      { name: 'Léa Martin', seed: 'demo-seed-lea' },
      { name: 'Nour Benali', seed: 'demo-seed-nour' },
      { name: 'Tom Girard', seed: 'demo-seed-tom' },
    ].map((p, i) =>
      h(
        'div',
        { class: `demo-person p${i + 1}` },
        initialsAvatar(p.name, p.seed, 'demo-person-avatar'),
        h('span', { class: 'demo-check' }, icon('check', 12)),
      ),
    ),
  );

  return h('div', { class: 'demo', 'aria-hidden': 'true' }, miniCard, flow, people);
}

/** The privacy slide: you → a relay that only ever sees noise → your contact. */
function blindRelayDemo(): HTMLElement {
  const node = (label: string, ic: IconName): HTMLElement =>
    h('div', { class: 'relay-node' }, h('span', { class: 'relay-badge' }, icon(ic, 18)), h('span', { class: 'relay-label', text: label }));

  return h(
    'div',
    { class: 'relay-demo', 'aria-hidden': 'true' },
    node('Vous', 'user'),
    h('div', { class: 'relay-wire' }, h('span', { class: 'relay-spark' })),
    h(
      'div',
      { class: 'relay-server' },
      h('span', { class: 'relay-server-label', text: 'Le serveur' }),
      h('span', { class: 'relay-cipher mono', text: 'k3v9…q1zR' }),
      h('span', { class: 'relay-server-note', text: 'ne voit que ça' }),
    ),
    h('div', { class: 'relay-wire' }, h('span', { class: 'relay-spark relay-spark-2' })),
    node('Votre proche', 'users'),
  );
}

// --- Steps ------------------------------------------------------------------

interface Ctx {
  go: (step: StepName, dir?: 1 | -1) => void;
  state: { mnemonic: string; firstName: string; circleId: string };
  identityPromise: () => Promise<PreparedIdentity>;
  invited: string | null;
}

function heroStep(ctx: Ctx): HTMLElement {
  const title = ctx.invited
    ? 'Quelqu’un veut partager ses coordonnées avec vous'
    : 'Vos coordonnées, toujours à jour chez vos proches';
  const sub = ctx.invited
    ? 'Cercle est un carnet d’adresses vivant. Créez le vôtre en une minute pour recevoir sa fiche — et chacune de ses mises à jour.'
    : 'Vous corrigez votre adresse une fois. Tous vos proches l’ont aussitôt. Chiffré de bout en bout, sans compte ni mot de passe.';

  return h(
    'div',
    { class: 'onb-step onb-hero' },
    h('div', { class: 'onb-hero-mark' }, brand('lg')),
    ctx.invited ? eyebrow('Invitation reçue') : eyebrow('Bienvenue'),
    stepTitle(title),
    stepLead(sub),
    updateDemo(),
    h(
      'div',
      { class: 'onb-actions' },
      primaryButton('Découvrir Cercle', () => ctx.go('value'), 'arrow'),
      ghostButton('J’ai déjà une phrase de récupération', () => navigate({ name: 'recover' }), 'key'),
    ),
  );
}

function valueStep(ctx: Ctx): HTMLElement {
  return h(
    'div',
    { class: 'onb-step' },
    eyebrow('Ce que ça change'),
    stepTitle('Un carnet d’adresses qui se tient à jour tout seul'),
    updateDemo(),
    stepLead(
      'Vous déménagez, vous changez de numéro ? Vous modifiez votre fiche une seule fois : elle est aussitôt à jour chez tous vos proches. Aucun message à envoyer, aucun carnet à corriger.',
    ),
    h('div', { class: 'onb-actions' }, primaryButton('Ensuite', () => ctx.go('how'), 'arrow')),
  );
}

function howStep(ctx: Ctx): HTMLElement {
  return h(
    'div',
    { class: 'onb-step' },
    eyebrow('Comment ça marche'),
    stepTitle('Trois gestes, c’est tout'),
    h(
      'ul',
      { class: 'onb-bullets onb-stagger' },
      bullet('edit', 'Vous remplissez votre fiche', 'Nom, téléphone, adresses… Vous êtes la seule personne à pouvoir l’écrire.'),
      bullet('qr', 'Vous ajoutez vos proches', 'Chacun ajoute l’autre : vous ajoutez son ID Cercle, il ajoute le vôtre. Un QR code à scanner ou un lien à envoyer suffit.'),
      bullet('refresh', 'Tout reste à jour', 'À chaque modification, la nouvelle version part toute seule chez eux.'),
    ),
    h('div', { class: 'onb-actions' }, primaryButton('Ensuite', () => ctx.go('privacy'), 'arrow')),
  );
}

function privacyStep(ctx: Ctx): HTMLElement {
  return h(
    'div',
    { class: 'onb-step' },
    eyebrow('Votre vie privée'),
    stepTitle('Lisible par vos proches. Par personne d’autre.'),
    blindRelayDemo(),
    h(
      'ul',
      { class: 'onb-bullets onb-stagger' },
      bullet('lock', 'Chiffré sur votre appareil', 'Vos coordonnées voyagent chiffrées : le serveur qui les transporte ne peut pas les lire.'),
      bullet('user', 'Aucun compte à créer', 'Pas d’email, pas de mot de passe. Votre identité vit sur cet appareil, avec une phrase de récupération à noter une fois.'),
      bullet('trash', 'Vous gardez la main', 'Vous retirez quelqu’un de votre Cercle ? Sa copie de vos coordonnées s’efface et il ne reçoit plus rien.'),
    ),
    h('div', { class: 'onb-actions' }, primaryButton('Commencer', () => ctx.go('you'), 'arrow')),
  );
}

function youStep(ctx: Ctx): HTMLElement {
  const inputs = new Map<string, HTMLInputElement>();
  const textField = (key: string, label: string, type = 'text', extra: Record<string, string> = {}): HTMLElement => {
    const input = h('input', { type, autocomplete: 'off', ...extra }) as HTMLInputElement;
    inputs.set(key, input);
    return h('label', { class: 'form-field' }, h('span', { class: 'field-label', text: label }), input);
  };

  const firstEl = textField('firstName', 'Prénom', 'text', { autocapitalize: 'words', autocomplete: 'given-name' });
  const lastEl = textField('lastName', 'Nom', 'text', { autocapitalize: 'words', autocomplete: 'family-name' });
  const emailEl = textField('email', 'Email (facultatif)', 'email', { autocapitalize: 'none', autocomplete: 'email' });
  const phoneEl = textField('phone', 'Téléphone (facultatif)', 'tel', { autocomplete: 'tel' });

  const first = inputs.get('firstName')!;
  const last = inputs.get('lastName')!;

  // A live preview of the card their friends will see: initials fill in as they
  // type, on the colour their real Circle ID will produce once it's derived.
  let seed = 'cercle-preview';
  const preview = h('div', { class: 'onb-preview' });
  const nameLine = h('div', { class: 'onb-preview-name', text: 'Votre nom' });
  const subLine = h('div', { class: 'small muted', text: 'Ce que vos proches verront' });
  const paint = (): void => {
    const name = [first.value.trim(), last.value.trim()].filter(Boolean).join(' ');
    preview.replaceChildren(
      h('div', { class: 'avatar-ring' }, initialsAvatar(name, seed, 'avatar onb-preview-avatar')),
      h('div', {}, nameLine, subLine),
    );
    nameLine.textContent = name || 'Votre nom';
  };
  paint();
  void ctx.identityPromise().then((p) => {
    seed = p.circleId;
    paint();
  });

  const create = primaryButton('Créer mon Cercle', () => void submit(), 'check');
  create.disabled = true;
  const sync = (): void => {
    create.disabled = !first.value.trim();
  };
  for (const el of inputs.values()) el.addEventListener('input', el === first || el === last ? () => { paint(); sync(); } : sync);
  first.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') last.focus();
  });

  let submitting = false;
  const submit = async (): Promise<void> => {
    const firstName = first.value.trim();
    if (!firstName || submitting) return;
    submitting = true;
    create.disabled = true;
    try {
      const prepared = await ctx.identityPromise();
      await adoptIdentity(prepared);
      ctx.state.mnemonic = prepared.mnemonic;
      ctx.state.firstName = firstName;
      ctx.state.circleId = prepared.circleId;
      await saveMyProfile({
        ...emptyProfile(),
        firstName,
        lastName: last.value.trim(),
        email: inputs.get('email')!.value.trim(),
        phone: inputs.get('phone')!.value.trim(),
      });
      ctx.go('phrase');
    } catch (e) {
      submitting = false;
      create.disabled = false;
      toast('Erreur : ' + (e as Error).message);
    }
  };

  const step = h(
    'div',
    { class: 'onb-step' },
    eyebrow('Votre fiche'),
    stepTitle('Faisons connaissance'),
    stepLead('C’est le nom sous lequel vos proches vous verront. Le reste peut attendre — tout se complète et se corrige à tout moment.'),
    preview,
    h('div', { class: 'card onb-form' }, h('div', { class: 'row' }, firstEl, lastEl), emailEl, phoneEl),
    h('div', { class: 'onb-actions' }, create),
  );
  // Focus the first field on arrival, so the keyboard is already up on mobile.
  requestAnimationFrame(() => first.focus());
  return step;
}

function phraseStep(ctx: Ctx): HTMLElement {
  const words = ctx.state.mnemonic.split(' ').filter(Boolean);
  const grid = h(
    'div',
    { class: 'phrase' },
    ...words.map((w, i) =>
      h('div', { class: 'word' }, h('span', { class: 'word-n', text: String(i + 1) }), h('span', { class: 'word-t', text: w })),
    ),
  );

  const confirmed = h('input', { type: 'checkbox', id: 'confirm' }) as HTMLInputElement;
  const done = primaryButton('C’est noté, continuer', () => void finish(), 'arrow');
  done.disabled = true;
  confirmed.addEventListener('change', () => {
    done.disabled = !confirmed.checked;
  });

  const finish = async (): Promise<void> => {
    await markSeedBackedUp();
    ctx.go('ready');
  };

  return h(
    'div',
    { class: 'onb-step' },
    eyebrow('Dernière étape'),
    stepTitle('Votre phrase de récupération'),
    stepLead(
      'Ces mots sont le seul moyen de retrouver votre Cercle sur un autre appareil. Notez-les dans l’ordre, sur papier : comme la combinaison d’un coffre, personne ne peut vous les redonner.',
    ),
    grid,
    ghostButton('Copier la phrase', async () => {
      try {
        await navigator.clipboard.writeText(ctx.state.mnemonic);
        toast('Phrase copiée — collez-la dans un gestionnaire sûr');
      } catch {
        toast('Copie impossible sur ce navigateur');
      }
    }, 'copy'),
    h(
      'div',
      { class: 'notice notice-danger' },
      h('span', {
        text: 'Ne la montrez à personne. Perdue avec cet appareil, votre Cercle l’est aussi : il n’existe aucune récupération par email.',
      }),
    ),
    h('label', { class: 'checkbox' }, confirmed, h('span', { text: 'J’ai noté ma phrase de récupération en lieu sûr' })),
    h('div', { class: 'onb-actions' }, done),
  );
}

function readyStep(ctx: Ctx): HTMLElement {
  const me = currentIdentity();
  const circleId = me?.circleId ?? ctx.state.circleId;
  const name = ctx.state.firstName;

  const addContact = (): void => {
    navigate({ name: 'circle' });
    // Coming from an invite link, the Cercle screen consumes the pending
    // invite itself (adds straight away if they're already a friend, else
    // opens the sheet prefilled) — opening one here too would stack a
    // second copy on top of it.
    if (ctx.invited) return;
    // The sheet is a body-level overlay, so it survives the navigation; adding
    // re-navigates to the same route, which re-renders the list underneath.
    requestAnimationFrame(() => openAddFriendSheet('', () => navigate({ name: 'circle' })));
  };

  return h(
    'div',
    { class: 'onb-step onb-ready' },
    h('div', { class: 'onb-seal' }, icon('check', 30)),
    eyebrow('Votre Cercle est ouvert'),
    stepTitle(name ? `Tout est prêt, ${name}` : 'Tout est prêt'),
    stepLead(
      ctx.invited
        ? 'Voici votre fiche. Ajoutez maintenant la personne qui vous a invité·e : elle a votre ID Cercle, il vous manque le sien.'
        : 'Voici votre fiche. Faites scanner ce code par un proche, puis ajoutez son ID Cercle à votre tour : chacun doit ajouter l’autre.',
    ),
    circleId ? shareCard(circleId) : h('p', { class: 'lead', text: 'Identité introuvable.' }),
    h(
      'div',
      { class: 'onb-actions' },
      primaryButton(ctx.invited ? 'Ajouter cette personne' : 'Ajouter un proche', addContact, 'plus'),
      ghostButton('Voir mon profil', () => navigate({ name: 'dashboard' }), 'user'),
    ),
  );
}

// --- The screen -------------------------------------------------------------

export function OnboardingScreen(): HTMLElement {
  const state = { mnemonic: '', firstName: '', circleId: '' };
  const invited = pendingInvite();

  // Key derivation starts as soon as the visitor engages with the intro, so the
  // "Créer mon Cercle" tap resolves instantly. Nothing is written until then.
  let prepared: Promise<PreparedIdentity> | null = null;
  const identityPromise = (): Promise<PreparedIdentity> => (prepared ??= prepareIdentity(128));

  let index = 0;

  const stage = h('div', { class: 'onb-stage' });
  const progress = h('div', { class: 'onb-progress' });
  const back = h('button', {
    class: 'back-btn onb-back',
    type: 'button',
    'aria-label': 'Étape précédente',
    onclick: () => go(FLOW[index - 1]!, -1),
  });
  back.append(icon('back', 20));
  const skip = h('button', { class: 'onb-skip', type: 'button', text: 'Passer', onclick: () => go('you') });
  const top = h('div', { class: 'onb-top' }, back, brand(), skip);

  const ctx: Ctx = { go: (step, dir) => go(step, dir), state, identityPromise, invited };

  const build = (name: StepName): HTMLElement => {
    switch (name) {
      case 'hero':
        return heroStep(ctx);
      case 'value':
        return valueStep(ctx);
      case 'how':
        return howStep(ctx);
      case 'privacy':
        return privacyStep(ctx);
      case 'you':
        return youStep(ctx);
      case 'phrase':
        return phraseStep(ctx);
      case 'ready':
        return readyStep(ctx);
    }
  };

  function go(name: StepName, dir: 1 | -1 = 1): void {
    const next = FLOW.indexOf(name);
    if (next < 0) return;
    index = next;
    if (index > 0) void identityPromise();

    const el = build(name);
    el.classList.add(dir === 1 ? 'onb-enter-next' : 'onb-enter-prev');
    stage.replaceChildren(el);

    // Chrome: no back/skip on the hero; once the identity exists there is no
    // going back (the phrase can only be shown once) and nothing to skip.
    const committed = name === 'phrase' || name === 'ready';
    back.classList.toggle('is-hidden', index === 0 || committed);
    skip.classList.toggle('is-hidden', !(name === 'value' || name === 'how' || name === 'privacy'));
    // The hero carries its own large wordmark, so the chrome's small one would
    // just be the same name twice.
    top.classList.toggle('is-hidden', index === 0);
    progress.replaceChildren(
      ...FLOW.slice(1).map((_, i) => h('span', { class: 'onb-seg' + (i <= index - 1 ? ' is-done' : '') })),
    );
    progress.classList.toggle('is-hidden', index === 0);

    window.scrollTo({ top: 0 });
    if (index > 0) (el.querySelector('.onb-title') as HTMLElement | null)?.focus({ preventScroll: true });
  }

  // Swipe between the three intro slides — the gesture people already try.
  let startX = 0;
  let startY = 0;
  let tracking = false;
  stage.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length !== 1 || !SWIPEABLE.includes(FLOW[index]!)) return;
      startX = e.touches[0]!.clientX;
      startY = e.touches[0]!.clientY;
      tracking = true;
    },
    { passive: true },
  );
  stage.addEventListener(
    'touchend',
    (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return; // vertical scroll wins
      if (dx < 0) go(FLOW[index + 1]!, 1);
      else go(FLOW[index - 1]!, -1);
    },
    { passive: true },
  );

  const root = h('div', { class: 'onb' }, top, progress, stage);

  // Arrow keys walk the intro on desktop. Self-cleaning: the listener drops
  // itself once this screen has been mounted and later replaced.
  let mounted = false;
  const onKey = (e: KeyboardEvent): void => {
    if (root.isConnected) mounted = true;
    else if (mounted) {
      window.removeEventListener('keydown', onKey);
      return;
    }
    if (!SWIPEABLE.includes(FLOW[index]!)) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    if (e.key === 'ArrowRight') go(FLOW[index + 1]!, 1);
    else if (e.key === 'ArrowLeft') go(FLOW[index - 1]!, -1);
  };
  window.addEventListener('keydown', onKey);

  go('hero');
  return root;
}
